import { XCircleIcon } from '@heroicons/react/24/outline';
import { type WorkQueueCase } from '../../../../services/workQueue';
import { declineTreatment } from '../../../../services/approval';
import { ConfirmModalShell } from './ConfirmModalShell';
import { ConfirmModalHeader } from './ConfirmModalHeader';
import { ConfirmModalBody } from './ConfirmModalBody';
import { ConfirmModalFooter } from './ConfirmModalFooter';
import { PatientSummaryCard } from './PatientSummaryCard';
import { ReasonField } from './ReasonField';
import { REASON_MAX_LENGTH, REASON_MIN_LENGTH } from './reasonLimits';
import { useReasonActionSubmit } from './useReasonActionSubmit';

interface DeclineCaseModalProps {
    workCase: WorkQueueCase;
    onClose: () => void;
    onSuccess: (actionId: string) => void;
}

function DeclineConsequenceNotice() {
    return (
        <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            This marks the case as declined by the patient. It can be reactivated later - by an ODO
            admin from the case detail panel, or by the practice from their dashboard.
        </p>
    );
}

export function DeclineCaseModal({ workCase: c, onClose, onSuccess }: DeclineCaseModalProps) {
    const { reason, setReason, submitting, canSubmit, confirm } = useReasonActionSubmit(
        c,
        onSuccess,
        {
            run: declineTreatment,
            successToast: `Case declined: ${c.name}`,
            fallbackMessage: 'Failed to decline case. Please try again.',
            source: 'DeclineCaseModal.handleConfirm',
            action: 'decline-treatment'
        }
    );

    return (
        <ConfirmModalShell widthClassName="w-[460px]" dismissible={!submitting} onDismiss={onClose}>
            <ConfirmModalHeader
                icon={XCircleIcon}
                iconClassName="w-5 h-5 text-red-500"
                title="Decline case"
                onClose={onClose}
                disabled={submitting}
            />

            <ConfirmModalBody className="space-y-3">
                <PatientSummaryCard name={c.name} practice={c.practice} />
                <DeclineConsequenceNotice />
                <ReasonField
                    value={reason}
                    onChange={setReason}
                    disabled={submitting}
                    label="Reason for declining"
                    placeholder="e.g. Patient withdrew consent, or treatment is no longer indicated."
                    minLength={REASON_MIN_LENGTH}
                    maxLength={REASON_MAX_LENGTH}
                />
            </ConfirmModalBody>

            <ConfirmModalFooter
                onCancel={onClose}
                onConfirm={confirm}
                cancelDisabled={submitting}
                confirmDisabled={!canSubmit}
                confirmLabel={submitting ? 'Declining…' : 'Confirm decline'}
                confirmColorClassName="bg-red-600 text-white hover:bg-red-700"
            />
        </ConfirmModalShell>
    );
}
